import * as React from 'react';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { Close } from '@mui/icons-material';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getNestedFormErrors } from 'src/shared/functions/getNestedFormErrors';

type ControlledAutocompleteDropdownValueLazyProps<T> = {
  nameProp: string;
  label: string;
  name: string;
  value: string;
  fetchMethod: () => Promise<T[]>;
  callback: (selected: any[] | undefined) => void;
  componentWidth?: string;
};

export const ControlledAutocompleteDropdownValueLazy = <T,>(props: ControlledAutocompleteDropdownValueLazyProps<T>) => {
  const { nameProp, label, name, value, fetchMethod, callback, componentWidth = '15rem' } = props;
  const [selectedValue, setSelectedValue] = React.useState<any[] | undefined>();
  const [inputValue, setInputValue] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly T[]>([]);
  const loading = open && options.length === 0;

  const isMounted = useIsMounted();

  const { control } = useFormContext();
  const { errors } = useFormState();
  const { setValue, getValues } = useFormContext();

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    if (!options.length) {
      (async () => {
        const result = await fetchMethod();

        if (active && isMounted()) {
          setOptions([...result]);
        }
      })();
    }

    return () => {
      active = false;
    };
  }, [fetchMethod, isMounted, loading, options.length]);

  // React.useEffect(() => {
  //   if (!open && isMounted()) {
  //     setOptions([]);
  //   }
  // }, [open, isMounted]);

  const componentId = `autocomplete-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  let isError = false;
  const errorMessage = getNestedFormErrors(errors, name ?? '');
  if (errorMessage) {
    isError = true;
  }

  React.useEffect(() => {
    const fromState = getValues(name);
    if (fromState) {
      setSelectedValue(fromState);
    }
  }, [getValues, name]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          id={componentId}
          options={options}
          autoHighlight
          open={open}
          openOnFocus
          // disableCloseOnSelect
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          loading={loading}
          getOptionLabel={(option) => option[nameProp] || ''}
          onChange={(event: any, newValue: any) => {
            setValue(name, newValue.value);
            setSelectedValue(newValue);
            callback(newValue);
          }}
          value={selectedValue || null}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              sx={{ width: componentWidth }}
              label={label}
              {...field}
              error={isError}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'off', // disable autocomplete and autofill. new-password Not working in current chrome, off seems to work.
              }}
              helperText={errorMessage ? <em>{errorMessage}</em> : null}
            />
          )}
          clearIcon={<Close fontSize='small' />}
          sx={{
            '& .MuiChip-sizeMedium ': { height: 'auto', borderRadius: 1 },
            '& .MuiChip-deleteIcon ': { fontSize: '18px' },
          }}
        />
      )}
    />
  );
};
